<template>
  <layout-default>
    <div class="participants-dashboard">
      <v-overlay :value="loading" z-index="4">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
      <summary-card
        :showSwaps="isSwapAvailable"
        :showJournal="isJournalAvailable"
        :filter="defaultQueryParams.range"
        :filter-options="availableQueryParams.range"
        :sort="currentSorting"
        :sorting-options="sortingOptions"
        @change-filter="changeFilter"
        @change-sort="changeSort"
        :participants="summaryData.participants"
        :positiveActions="summaryData.positiveActions"
        :avgDailyRating="summaryData.avgDailyRating"
        :totalDailyRating="summaryData.totalDailyRating"
        :journalEntries="summaryData.journalEntries"
        :swapsAddedTo="summaryData.swapsAddedTo"
        :swapsCompleted="summaryData.swapsCompleted"
        :swapsInProgress="summaryData.swapsInProgress"
      />
      <div class="d-flex justify-end px-2">
        <v-checkbox
          label="Include all participants"
          @change="value => changeShowAll(value)"
          v-model="defaultQueryParams.showAll"
        />
      </div>
      <div class="participants-list mt-6">
        <template v-if="isDashboardContent">
          <participant-list-item
            v-for="participant in participants"
            :key="`participant-${participant.participantId}`"
            :showSwaps="isSwapAvailable"
            :showJournal="isJournalAvailable"
            :unreadChatMessages="unreadChatMessages(participant.chatId)"
            v-bind="participant"
            @navigate-to-participant="navigateToParticipant"
          />
        </template>
      </div>
    </div>
  </layout-default>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex'
import { debounce } from '@/helpers/debounce'
import jwt_decode from 'jwt-decode'
import LayoutDefault from '../layouts/LayoutDefault.vue'
import RouterMixin from '@/mixins/router'
import FeaturesSettingsMixin from '@/mixins/featuresSettings'
import SummaryCard from '../components/participantsDashboard/SummaryCard.vue'
import ParticipantListItem from '../components/participantsDashboard/ParticipantListItem.vue'
import TenantTextsMixin, {
  tenantTexts as normalTenentTexts,
} from '@/mixins/tenantTexts'

export default {
  name: 'ParticipantsDashboard',
  components: {
    LayoutDefault,
    SummaryCard,
    ParticipantListItem,
  },
  mixins: [TenantTextsMixin, RouterMixin, FeaturesSettingsMixin],
  props: {
    profileId: {
      type: Number,
      require: false,
      default: undefined,
    },
    isCompanyAdmin: {
      type: Boolean,
      require: false,
      default: false,
    },
  },
  data() {
    return {
      defaultQueryParams: {
        range: 'week',
        showAll: false,
        profileId: this.profileId,
        isCompanyAdmin: this.isCompanyAdmin,
      },
      availableQueryParams: {
        range: [
          { text: 'Today', value: 'today' },
          { text: 'Week', value: 'week' },
          { text: 'Month', value: 'month' },
          { text: 'Year', value: 'year' },
        ],
      },
      currentSorting: 'lastActivitySort',
      sortingOptions: [
        { text: 'Participants Alphabetized', value: 'fullnameSort' },
        { text: 'Total', value: 'totalSort' },
        { text: 'Unresponded Total', value: 'unrespondedTotalSort' },
        {
          text: normalTenentTexts.participantsDashboard.routinesSkipped,
          value: 'routinesSkippedSort',
        },
        { text: 'The most recent engagement', value: 'lastActivitySort' },
      ],
      journalAvailable: 'Active',
      swapAvailable: 'Active',
    }
  },
  computed: {
    ...mapGetters('dictionariesStates', ['getMimeTypes']),
    ...mapState('chatStates', ['unreadMessagesObject']),
    ...mapGetters('userStates', ['getRolename', 'getToken']),
    ...mapState('patientsStates', ['participantsDashboard', 'loading']),
    ...mapState('userStates', ['user', 'userId', 'userFeatureFlags']),
    ...mapState('dictionariesStates', [
      'dictionariesLoaded',
      'dictionaryPracticeUserRole',
      'dictionaryBhUsersRoleIds',
      'dictionaryFeatureFlagState',
    ]),
    // @todo Heroic choice, and journal
    isJournalAvailable() {
      return (
        this.userFeatureFlags.journalAvailable !==
        this.dictionaryFeatureFlagState[
          this.dictionaryFeatureFlagState.Inactive
        ]
      )
    },
    isSwapAvailable() {
      return (
        this.userFeatureFlags.swapAvailable !==
        this.dictionaryFeatureFlagState[
          this.dictionaryFeatureFlagState.Inactive
        ]
      )
    },
    isDashboardContent() {
      return !!this.participantsDashboard?.content?.length
    },
    summaryData() {
      let summaryData = {
        participants: 0,
        positiveActions: 0,
        avgDailyRating: 0,
        totalDailyRating: 0,
        journalEntries: 0,
        swapsAddedTo: 0,
        swapsCompleted: 0,
        swapsInProgress: 0,
      }

      // Return zeros when no data
      if (!this.isDashboardContent) return summaryData

      // Sum all required values
      summaryData = this.participantsDashboard.content.reduce((acc, curVal) => {
        return {
          participants: acc.participants + 1,
          positiveActions: acc.positiveActions + (curVal?.routinesTotal || 0),
          avgDailyRating: acc.avgDailyRating + (curVal?.ratingDaySum || 0),
          totalDailyRating:
            acc.totalDailyRating + (curVal?.ratingDayCount || 0),
          journalEntries: acc.journalEntries + (curVal?.journalEntries || 0),
          swapsAddedTo: acc.swapsAddedTo + (curVal?.swapActivitiesTotal || 0),
          swapsCompleted: acc.swapsCompleted + (curVal?.swapCompleted || 0),
          swapsInProgress: acc.swapsInProgress + (curVal?.swapInProgress || 0),
        }
      }, summaryData)

      if (summaryData.totalDailyRating > 0) {
        summaryData.avgDailyRating =
          summaryData.avgDailyRating / summaryData.totalDailyRating
      }

      return summaryData
    },
    participants() {
      if (!this.isDashboardContent) return []

      const allParticipants = [...this.participantsDashboard.content]

      const sortedParticipants = allParticipants.sort((a, b) => {
        let Afullname = ''
        let Bfullname = ''

        let AunreadMsg = this.unreadMessagesObject?.[a?.chatId] || 0
        let BunreadMsg = this.unreadMessagesObject?.[b?.chatId] || 0

        let AlastActivity = this.$moment(a?.lastActivity || '2000-01-01')
        let BlastActivity = this.$moment(b?.lastActivity || '2000-01-01')
        let DiffLastActivity = AlastActivity.diff(BlastActivity)

        switch (this.currentSorting) {
          case 'fullnameSort':
            Afullname = a.firstname
            if (Afullname.length > 0) Afullname += ' '
            Afullname += a.lastname
            Bfullname = b.firstname
            if (Bfullname.length > 0) Bfullname += ' '
            Bfullname += b.lastname

            if (Afullname < Bfullname) return -1
            if (Afullname > Bfullname) return 1
            return 0
          case 'totalSort':
            if (a.total + AunreadMsg < b.total + BunreadMsg) return 1
            if (a.total + AunreadMsg > b.total + BunreadMsg) return -1
            return 0
          case 'unrespondedTotalSort':
            if (
              a.unrespondedTotal + AunreadMsg <
              b.unrespondedTotal + BunreadMsg
            )
              return 1
            if (
              a.unrespondedTotal + AunreadMsg >
              b.unrespondedTotal + BunreadMsg
            )
              return -1
            return 0
          case 'routinesSkippedSort':
            if (a.routinesSkipped < b.routinesSkipped) return 1
            if (a.routinesSkipped > b.routinesSkipped) return -1
            return 0
          case 'lastActivitySort':
            if (DiffLastActivity > 0) return -1
            if (DiffLastActivity < 0) return 1
            return 0
        }
      })

      return sortedParticipants
    },
  },
  mounted() {
    // redirect if cannot go to the dashboard
    if (
      this.dictionaryPracticeUserRole[this.getRolename] !== 5 &&
      !this.profileId
    ) {
      this.$router.push('/participants')
      return
    }

    this.getData()
  },
  methods: {
    ...mapActions('patientsStates', ['getParticipantsDashboard']),
    ...mapActions('practicesStates', ['getRawPractice']),
    getData() {
      // redirect if cannot go to the dashboard
      if (
        this.dictionaryPracticeUserRole[this.getRolename] !== 5 &&
        !this.profileId
      ) {
        this.$router.push('/participants')
        return
      }

      debounce(
        () => {
          this.getFeatureFlagsFromPractice()
          this.getParticipantsDashboard(this.defaultQueryParams)
        },
        100,
        'getPatientsDashboard',
      )
    },
    async getFeatureFlagsFromPractice() {
      try {
        const practiceId = this.getPracticeId()
        if (!practiceId) return
        const practice = await this.getRawPractice(practiceId)
        if (practice) {
          const featureFlagState = Object.keys(this.dictionaryFeatureFlagState)
          if (practice.journalAvailable)
            practice.journalAvailable =
              featureFlagState.find(
                flagState =>
                  flagState.toLowerCase() ===
                  practice.journalAvailable.toLowerCase(),
              ) || practice.journalAvailable
          if (practice.swapAvailable)
            practice.swapAvailable =
              featureFlagState.find(
                flagState =>
                  flagState.toLowerCase() ===
                  practice.swapAvailable.toLowerCase(),
              ) || practice.swapAvailable
          this.journalAvailable = practice.journalAvailable || 'Active'
          this.swapAvailable = practice.swapAvailable || 'Active'
        }
      } catch (err) {
        console.error('Cannot get feature flags settings from practice', {
          err,
        })
        this.journalAvailable = 'Active'
        this.swapAvailable = 'Active'
      }
    },
    getPracticeId() {
      if (this.getToken && this.dictionaryPracticeUserRole[this.getRolename]) {
        const decodedToken = jwt_decode(this.getToken) || {}

        return parseInt(decodedToken.practice_id, 10) || null
      }

      return null
    },
    unreadChatMessages(chatId = '') {
      return this.unreadMessagesObject?.[chatId] || 0
    },
    changeSort(sort = '') {
      let selectedSort =
        this.sortingOptions.find(el => el.value === sort)?.value || ''

      if (!selectedSort) selectedSort = 'lastActivitySort'

      this.currentSorting = selectedSort
    },
    changeFilter(filter = '') {
      let selectedFilter =
        this.availableQueryParams.range.find(el => el.value === filter)
          ?.value || ''

      if (!selectedFilter) selectedFilter = 'week'

      this.defaultQueryParams.range = selectedFilter

      this.getParticipantsDashboard(this.defaultQueryParams)
    },
    changeShowAll(value = false) {
      const showAll = value === true || value === 'true'

      this.defaultQueryParams.showAll = showAll

      this.getParticipantsDashboard(this.defaultQueryParams)
    },
    navigateToParticipant(participantId, type) {
      if (!participantId) return

      let route = false

      switch (type) {
        case 'chat':
          route = {
            name: 'Patient Chat',
            path: '/participants/:id/chat',
            params: {
              id: participantId,
            },
          }
          break
        case 'swaps':
          route = {
            name: 'Participant Swaps',
            path: '/participants/:id/swaps',
            params: {
              id: participantId,
            },
          }
          break
        case 'routines':
          route = {
            name: 'Patient Exercises',
            path: '/participants/:id/routines',
            params: {
              id: participantId,
            },
          }
          break
        case 'journal':
          route = {
            name: 'Patient Journal',
            path: '/participants/:id/journal',
            params: {
              id: participantId,
            },
          }
          break
        case 'courses':
          route = {
            name: 'Patient Courses',
            path: '/participants/:id/courses',
            params: {
              id: participantId,
            },
          }
          break
        default:
          route = {
            name: 'Patient',
            path: '/participants/:id',
            params: { id: participantId },
          }
      }
      this.$router.push(route)
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.participants-dashboard {
  padding: 1.5 * $gutter $gutter 0 $gutter;

  .participants-list {
    padding-left: 0.5 * $gutter;
    padding-right: 0.5 * $gutter;
  }
}
</style>
